// src/pages/ProjectDetail.js
import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { getProjectById } from "../data/projects";
import stillsManifest from "../data/stillsManifest.json";

// "LYNCHIAN HORROR" -> "Lynchian Horror". Mixed-case text (e.g. "OCD Short Film")
// is left alone so acronyms survive.
const tidyCase = (text = "") =>
  text === text.toUpperCase()
    ? text.toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase())
    : text;

const ProjectDetail = () => {
  const { id } = useParams();
  const project = getProjectById(id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl mb-4">Project not found</h1>
          <Link to="/work" className="underline hover:opacity-70">
            Back to Work
          </Link>
        </div>
      </div>
    );
  }

  // Stills, in order of priority:
  // 1. files dropped in public/images/projects/<id>/still-N.jpg (auto-detected
  //    by scripts/generate-stills.js at build time)
  // 2. project.stills in the data file
  // 3. the thumbnail repeated three times as a placeholder
  const droppedStills = stillsManifest[project.id];
  const stills =
    droppedStills && droppedStills.length > 0
      ? droppedStills
      : project.stills && project.stills.length > 0
      ? project.stills
      : [project.thumbnail, project.thumbnail, project.thumbnail];

  // Even number of stills -> 2 per row (2x2); otherwise 3 per row
  const stillsColumns =
    stills.length % 2 === 0 ? "sm:grid-cols-2" : "sm:grid-cols-3";

  const videoFrame = "absolute inset-0 w-full h-full";

  return (
    <div className="min-h-screen pt-24 md:pt-48 pb-24">
      <div className="mx-auto px-6 md:px-12">
        <div className="max-w-[1154px] mx-auto">
          {/* Description */}
          <div className="max-w-[860px] mx-auto text-center text-[13px] leading-[1.5]">
            <h1 className="font-sans text-[13px] font-bold tracking-[0.02em]">
              {project.title}
            </h1>
            {(project.description || "")
              .split(/\n\s*\n/)
              .filter(Boolean)
              .map((paragraph, index) => (
                <p key={index} className={`italic ${index > 0 ? "mt-5" : ""}`}>
                  {paragraph}
                </p>
              ))}

            {(project.subtitle || project.credit || project.role || project.info) && (
              <div className="mt-5">
                {project.subtitle && (
                  <p className="font-bold whitespace-pre-line">
                    {tidyCase(project.subtitle)}
                  </p>
                )}
                {project.credit ? (
                  <p>{project.credit}</p>
                ) : (
                  project.role && <p>{project.role}</p>
                )}
                {(project.info || []).map((line, index) => (
                  line === "" ? (
                    <div key={index} className="h-5" />
                  ) : (
                    <p key={index}>{line}</p>
                  )
                ))}
              </div>
            )}
          </div>

          {/* Stills */}
          <div
            className={`grid grid-cols-1 ${stillsColumns} gap-4 md:gap-6 mt-16`}
          >
            {stills.map((src, index) => (
              <div
                key={index}
                className="overflow-hidden bg-gray-900"
                style={{ aspectRatio: project.stillsAspect || "16 / 9" }}
              >
                <img
                  src={src}
                  alt={`${project.title} still ${index + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>

          {/* Video (hidden until a youtubeId, vimeoId or videoUrl is set) */}
          {(project.youtubeId || project.vimeoId || project.videoUrl) && (
          <div className="relative w-full aspect-[16/9] bg-black mt-10">
            {project.youtubeId ? (
              <iframe
                className={videoFrame}
                src={`https://www.youtube.com/embed/${project.youtubeId}?rel=0`}
                title={project.title}
                frameBorder="0"
                loading="lazy"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            ) : project.vimeoId ? (
              <iframe
                className={videoFrame}
                src={`https://player.vimeo.com/video/${project.vimeoId}${
                  project.vimeoHash ? `?h=${project.vimeoHash}` : ""
                }`}
                title={project.title}
                frameBorder="0"
                loading="lazy"
                allow="autoplay; picture-in-picture"
                allowFullScreen
              ></iframe>
            ) : (
              <video
                className={videoFrame}
                controls
                playsInline
                preload="metadata"
                poster={project.thumbnail}
              >
                <source src={project.videoUrl} type="video/mp4" />
              </video>
            )}
          </div>
          )}

          {/* Screener note (for projects with no public video) */}
          {project.screenerOnRequest &&
            !(project.youtubeId || project.vimeoId || project.videoUrl) && (
              <p className="mt-16 text-center text-[13px] leading-[1.5]">
                Screener available upon request. Email{" "}
                <a
                  href={`mailto:abdkokash.ak@gmail.com?subject=${encodeURIComponent(
                    `Screener request: ${project.title}`,
                  )}`}
                  className="underline hover:opacity-70 transition-opacity"
                >
                  abdkokash.ak@gmail.com
                </a>
              </p>
            )}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
